import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { jwtDecode } from 'jwt-decode';

const hoyStr = () => new Date().toISOString().slice(0, 10);

const CalendarioTurnos = () => {
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(hoyStr());
  const [estado, setEstado] = useState('aprobado');
  const [turnos, setTurnos] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [filtroDebounced, setFiltroDebounced] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  const verificarRol = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return false;
    }

    try {
      const decoded = jwtDecode(token);
      if (decoded.rol !== 'admin') {
        navigate('/');
        return false;
      }
      return true;
    } catch (err) {
      console.error('Error al validar sesion del administrador:', err);
      navigate('/login');
      return false;
    }
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(() => setFiltroDebounced(filtro), 250);
    return () => clearTimeout(t);
  }, [filtro]);

  const fetchTurnos = useCallback(async (f, e) => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const { data } = await api.get(
        `/turnos/porteria?fecha=${encodeURIComponent(f)}&estado=${encodeURIComponent(e || estado)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTurnos(
        (data.turnos || []).map((t) => ({
          ...t,
          _fecha: t.fecha_turno ? new Date(`${t.fecha_turno}T00:00:00`) : null,
        }))
      );
    } catch (e) {
      if (e?.code === 'ERR_CANCELED') return;
      const status = e?.response?.status;
      if (status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/');
        return;
      }
      if (status === 403) {
        navigate('/admin-turnos');
        return;
      }
      console.error('Error cargando turnos del administrador:', e);
      setError('No se pudieron cargar los turnos para esa fecha o estado.');
    } finally {
      setLoading(false);
    }
  }, [navigate, estado]);

  useEffect(() => {
    if (!verificarRol()) return;
    if (!fecha) setFecha(hoyStr());
    else fetchTurnos(fecha, estado);
  }, [fecha, estado, fetchTurnos, verificarRol]);

  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => fetchTurnos(fecha, estado), 60000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fecha, estado, fetchTurnos]);

  const handleHoy = useCallback(() => {
    setFecha(hoyStr());
  }, []);

  const diaDelta = useCallback((d) => {
    setFecha((prev) => {
      const x = new Date(`${prev}T00:00:00`);
      x.setDate(x.getDate() + d);
      return x.toISOString().slice(0, 10);
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') diaDelta(-1);
      if (e.key === 'ArrowRight') diaDelta(1);
      if (e.key.toLowerCase() === 'h') handleHoy();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [diaDelta, handleHoy]);

  const matchProveedorBultos = useCallback((pb = [], q) => {
    try {
      const ql = q.toLowerCase();
      return pb.some((p) =>
        (p?.nombre || '').toLowerCase().includes(ql) ||
        (Array.isArray(p?.facturas) ? p.facturas.join(',') : '').toLowerCase().includes(ql) ||
        (p?.remito || '').toLowerCase().includes(ql) ||
        (p?.orden_compra || '').toLowerCase().includes(ql)
      );
    } catch {
      return false;
    }
  }, []);

  const turnosFiltrados = useMemo(() => {
    const q = (filtroDebounced || '').trim().toLowerCase();
    if (!q) return turnos;
    return turnos.filter(
      (t) =>
        (t.transportista?.nombre || '').toLowerCase().includes(q) ||
        (t.observaciones || '').toLowerCase().includes(q) ||
        (estado === 'rechazado' ? (t.devolucion || '').toLowerCase().includes(q) : false) ||
        matchProveedorBultos(t.proveedor_bultos, q)
    );
  }, [turnos, filtroDebounced, matchProveedorBultos, estado]);

  const total = turnosFiltrados.length;
  const esRechazado = estado === 'rechazado';

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">
            Administrador - Turnos {esRechazado ? 'Rechazados' : 'Aprobados'}
          </h2>
          <small className="text-muted">Fecha seleccionada: {fecha}</small>
        </div>
        <div className="d-flex gap-2">
          <div className="form-check form-switch d-flex align-items-center me-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={() => setAutoRefresh((v) => !v)}
            />
            <label className="form-check-label ms-2" htmlFor="autoRefresh">
              Auto-refresh 60s
            </label>
          </div>

          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin-turnos')}>
            Volver
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Fecha</label>
              <input
                type="date"
                className="form-control"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Estado</label>
              <div className="btn-group w-100" role="group" aria-label="Estado">
                <button
                  type="button"
                  className={`btn ${estado === 'aprobado' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setEstado('aprobado')}
                >
                  Aprobados
                </button>
                <button
                  type="button"
                  className={`btn ${estado === 'rechazado' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setEstado('rechazado')}
                >
                  Rechazados
                </button>
              </div>
            </div>

            <div className="col-md-5">
              <label className="form-label">
                Buscar {esRechazado ? '(incluye motivo de rechazo)' : '(transportista, proveedor, factura, remito, OC)'}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={esRechazado ? 'Transportista, proveedor, motivo...' : 'Ej: Juan Perez, OC 4500..., Factura 0001-...'}
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
              />
            </div>

            <div className="col-md-12 d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={() => diaDelta(-1)}>Anterior</button>
              <button className="btn btn-outline-secondary" onClick={handleHoy}>Hoy</button>
              <button className="btn btn-outline-secondary" onClick={() => diaDelta(1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted">{loading ? 'Cargando...' : `Total: ${total}`}</div>
        <div className={`badge ${esRechazado ? 'bg-danger' : 'bg-success'}`}>
          {esRechazado ? 'Rechazados' : 'Aprobados'}
        </div>
        {filtroDebounced && <div className="badge bg-info text-dark">Filtro: "{filtroDebounced}"</div>}
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped text-center">
          <thead className="table-dark">
            <tr>
              <th>Hora Desde</th>
              <th>Hora Hasta</th>
              <th>Transportista</th>
              <th>{esRechazado ? 'Motivo Rechazo' : 'Observaciones'}</th>
              <th>Detalle Proveedor(es)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5">Cargando...</td></tr>
            ) : total === 0 ? (
              <tr><td colSpan="5">No hay turnos {esRechazado ? 'rechazados' : 'aprobados'} para la fecha.</td></tr>
            ) : (
              turnosFiltrados.map((t) => (
                <tr key={t.id}>
                  <td>{t.hora_desde?.slice(0, 5) || '-'}</td>
                  <td>{t.hora_hasta?.slice(0, 5) || '-'}</td>
                  <td>{t.transportista?.nombre || '-'}</td>
                  <td className={`text-start ${esRechazado ? 'text-danger fw-semibold' : ''}`}>
                    {esRechazado ? (t.devolucion || '-') : (t.observaciones || '-')}
                  </td>
                  <td className="text-start">
                    {(t.proveedor_bultos || []).length === 0 ? '-' : (
                      <ul className="mb-0">
                        {t.proveedor_bultos.map((p, i) => (
                          <li key={i}>
                            <strong>{p.nombre || 'Proveedor'}</strong>{' '}
                            {p.facturas?.length ? <>- Facturas: {p.facturas.join(', ')}</> : null}
                            {p.orden_compra ? <> - OC: {p.orden_compra}</> : null}
                            {p.remito ? <> - Remito: {p.remito}</> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CalendarioTurnos;
